import { Link } from "react-router-dom";
import MarketingShell from "./marketing/MarketingShell";
import { PUBLIC_ROUTES } from "../routes";

const BELIEFS = [
  {
    title: "Care is mostly the noticing.",
    body: "Most things that go wrong — a missed dose, a quiet decline, a fall on a Tuesday morning — go wrong because nobody noticed in time. We build the intelligence that makes a humanoid notice.",
  },
  {
    title: "The human is the point.",
    body: "We don&rsquo;t want anyone&rsquo;s daughter, carer, or line engineer replaced. We want them better-equipped, less interrupted, and called only when it matters.",
  },
  {
    title: "If you can&rsquo;t insure it, you can&rsquo;t ship it.",
    body: "Every humanoid team hits a wall at the front door of a real hospital or a private home. We build the safety, audit, and intelligence layers a robot needs to actually be allowed in — whoever made the robot.",
  },
] as const;

const FOUNDATION = [
  "Built on the RoboTwins world-model platform",
  "Sub-millisecond on-device safety reflex",
  "Cryptographic intent ledger (EU AI Act Article 12 record-keeping)",
  "Local Mind: pixels never leave the home",
  "ISO 25785-1 balance-operational alignment",
  "Underwriter-ready safety case from day one",
  "Closed-beta with named pilot partners only",
] as const;

export default function About() {
  return (
    <MarketingShell pageClass="sg-about-page">
      <section className="sg-about-hero">
        <p className="sg-eyebrow">About Manifold Kinetics</p>
        <h1>We don&rsquo;t build the robot. We build the part you have to trust.</h1>
        <p>
          Manifold Kinetics started inside a robotics team, building
          software that keeps factory robots safe around humans.
          Somewhere along the way we realised the harder problem
          isn&rsquo;t the factory floor — it&rsquo;s the kitchen at
          7:48am, where the robot is stronger and quicker than the
          person it is there for. Geoffrey Hinton gave that problem
          its name: the only model we have of a more capable mind
          being steered by a less capable one is a mother and her
          baby. So we let the humanoid makers make humanoids, and we
          build the part that decides what they do, stops them
          before they harm, and proves every choice — the part a
          family, a plant manager, and an insurer actually have to
          trust.
        </p>
        <div className="sg-hero-actions">
          <Link to={PUBLIC_ROUTES.contact} className="sg-btn sg-btn-primary">
            Request a pilot
          </Link>
          <Link to={PUBLIC_ROUTES.landing} className="sg-btn sg-btn-secondary">
            What we build
          </Link>
        </div>
      </section>

      <section className="sg-about-split">
        <div>
          <p className="sg-section-kicker">Why it exists</p>
          <h2>The hardest software in a humanoid is the part that knows when not to move.</h2>
        </div>
        <p>
          Arthur doesn&rsquo;t scold and doesn&rsquo;t diagnose.
          RoboTwins doesn&rsquo;t let a policy onto the floor until
          the envelope holds. Each has the discipline to do the small
          physical thing well and the bigger human decision never —
          to phone the daughter, to wait for the engineer. That
          discipline is what a mother has and what we engineer for.
          Most of what we ship is restraint.
        </p>
      </section>

      <section className="sg-pillars" aria-labelledby="beliefs-title">
        <div className="sg-section-heading compact">
          <p className="sg-section-kicker">What we believe</p>
          <h2 id="beliefs-title">Three things we take seriously.</h2>
        </div>
        <div className="sg-pillars-grid">
          {BELIEFS.map((b) => (
            <article className="sg-pillar" key={b.title}>
              <h3 dangerouslySetInnerHTML={{ __html: b.title }} />
              <p dangerouslySetInnerHTML={{ __html: b.body }} />
            </article>
          ))}
        </div>
      </section>

      <section className="sg-enterprise sg-about-stack">
        <div className="sg-enterprise-copy">
          <p className="sg-section-kicker">What it&rsquo;s built on</p>
          <h2>Robotics-grade engineering, applied to people.</h2>
          <p>
            The plumbing under Arthur is the same platform we built
            for industrial robots — because the standards we hold
            each other to in a factory cell turn out to be the right
            standards for a kitchen at home. And, as it happens, the standards an insurer asks
            for before letting any of it through the door.
          </p>
        </div>
        <div className="sg-enterprise-list">
          {FOUNDATION.map((item) => (
            <div key={item} className="sg-check-item">
              <span aria-hidden="true">✓</span>
              {item}
            </div>
          ))}
        </div>
      </section>

      <section className="sg-final-cta">
        <p className="sg-section-kicker">Pilots open now</p>
        <h2>Run a care provider or a production line?</h2>
        <p>
          We&rsquo;re looking for a small number of partners willing
          to start quietly, measure honestly, and tell us when
          we&rsquo;re wrong. If that sounds like you, we&rsquo;d
          love to talk.
        </p>
        <div className="sg-hero-actions center">
          <Link to={PUBLIC_ROUTES.contact} className="sg-btn sg-btn-primary">
            Request a pilot
          </Link>
          <Link to={PUBLIC_ROUTES.howItWorks} className="sg-btn sg-btn-secondary">
            How it works
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
